import { ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { MAT_CHECKBOX_DEFAULT_OPTIONS, MatCheckbox, MatCheckboxDefaultOptions } from '@angular/material/checkbox';
import { BlsGmbDifferencesLocalStore, BlsGmbDifferencesState, ColumnVisibilityModel, InfoMenuVisibilityModel } from './bls-gmb-differences-local-store.service';
import { CorrespondingBeValue, CorrespondingCheckCircleValue, DifferenceActions } from '../enums/difference-actions';
import { getChangeableGmbFields, getFullNameForFieldID, isLocationFieldBlacklisted } from '../location-utils';
import { BlsGmbDifference, DifferenceTranslationRequestPayload } from '../../../models/locations-models/bls-gmb-difference';
import { ALTERNATIVE_FIELD_IDS, COORDINATES_COMPARISON_FIELD_ID } from '../constants';
import { Store } from '@ngrx/store';
import { LSCField } from '../../../models/LSCFields';
import { Location } from '../../../models/locations-models/location';
import { map, take, takeUntil } from 'rxjs/operators';
import { memoize } from '../../../core/decorators/memoize';
import { flushRemainingDiffs, getFormattedDifferences, getStructuredDifferenceLines, prepareForDiffChecking, prepareUrlHref, wasOpenUrlClicked } from '../diff-utils';
import { combineLatestWith, Observable, Subject, switchMap } from 'rxjs';
import { SortingEnum } from '../enums/sorting-enum';
import { getDataSourceIconName, getDataSourceIconTooltipKey, isFieldWithDataSource } from '../data-source-utils';
import { DataSourceStatus } from '../enums/data-source-status';
import { capitalizeFirstLetter, isStringAnUrl } from '../../../core/utilities';
import { HttpErrorResponse } from '@angular/common/http';
import { FeedbackService } from '../../../core/feedback.service';
import { ApiService } from '../../../core/api-service';
import { LocationListService } from '../location-list/location-list.service';
import { cloneDeep } from 'lodash';
import { TableDataSource } from './table-data-source';
import { DISPLAYED_COLUMNS, getColumnDefinitions, TRANSLATABLE_ROWS } from './table-definitions';
import { Coordinate } from '../../../models/coordinate';
import { UpdateDifferencesPayload } from '../../../models/locations-models/update-differences-payload';
import { OngoingActionsEnum } from '../enums/ongoing-actions';
import { BlsGmbDifferenceMapService } from '../map/diff-map/bls-gmb-differences-map.service';
import { LocationsSkeletonService } from '../locations-skeleton.service';
import { JumperComponent } from '../../global/jumper/jumper.component';
import { blsGmbDifferenceRowAnimation } from '../animations/row-animation';
import * as locationListSelectors from '../location-list/state/location-list-selectors';
import * as skeletonSelectors from '../state/locations-skeleton-selectors';
import { selectAccountID } from '../../../store/global/core-selectors';
import { ApiResponseWithPotentialErrors } from '../../../models/locations-models/api-response-with-potential-errors';
import { LocationsRefreshActions, OngoingActions, RowActions } from '../location-list/state/location-list-actions';
import { LocationListLocalStoreService } from '../location-list/location-list-local-store.service';
import { LocationListApiService } from '../location-list/location-list-api.service';
import { TranslateModule } from '@ngx-translate/core';
import { BlsGmbDifferencesMapComponent } from '../map/diff-map/bls-gmb-differences-map';
import { MatDivider } from '@angular/material/divider';
import { GtagEventDirective } from '../../../core/gx-gtag/gtag-event.directive';
import { FormsModule } from '@angular/forms';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton, MatIconAnchor, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { ColumnSortingButtonComponent } from '../../global/column-sorting-button/column-sorting-button.component';
import { MatCell, MatCellDef, MatColumnDef, MatFooterCell, MatFooterCellDef, MatFooterRow, MatFooterRowDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatNoDataRow, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { LetDirective } from '@ngrx/component';
import { CoordinateSource } from '../../../models/coordinate-history';

@Component({
    selector: 'app-bls-gmb-differences',
    templateUrl: './bls-gmb-differences.component.html',
    styleUrls: ['./bls-gmb-differences.component.scss'],
    providers: [
        {
            provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
            useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions,
        },
        BlsGmbDifferencesLocalStore, BlsGmbDifferenceMapService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [blsGmbDifferenceRowAnimation],
    standalone: true,
    imports: [
        LetDirective,
        MatTable,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        ColumnSortingButtonComponent,
        MatCellDef,
        MatCell,
        NgClass,
        MatIcon,
        MatTooltip,
        MatIconButton,
        MatProgressSpinner,
        MatFooterCellDef,
        MatFooterCell,
        MatMenu,
        MatMenuTrigger,
        MatCheckbox,
        FormsModule,
        MatIconAnchor,
        GtagEventDirective,
        MatDivider,
        BlsGmbDifferencesMapComponent,
        MatButton,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatFooterRowDef,
        MatFooterRow,
        MatNoDataRow,
        JumperComponent,
        TranslateModule,
    ],
})

export class BlsGmbDifferencesComponent implements OnDestroy {

  @ViewChild('jumper') jumper: JumperComponent;

  tableDataSource: TableDataSource;
  componentDestroyed = new Subject<void>();
  viewModel$: Observable<{
    blsGmbDifferencesState: BlsGmbDifferencesState,
    focusedLocation: Location,
    total: number,
    isUnsavedChange: boolean
    updatingDiffsInProgress: boolean,
    updatingLocationListInProgress: boolean,
  }> = this.localStore.state$.pipe(
      combineLatestWith(
          this.store.select(locationListSelectors.selectFocusedLocation),
          this.store.select(skeletonSelectors.selectUnsavedChangesInBottomPanelTab),
          this.locationListLocalStore.total$,
          this.store.select(locationListSelectors.selectIsOngoingAction(OngoingActionsEnum.UPDATE_DIFFERENCES_AND_PATCH_GBP)),
          this.store.select(locationListSelectors.selectLoading),
      ),
      map(([blsGmbDifferencesState, focusedLocation, isUnsavedChange, total, updatingDiffsInProgress, updatingLocationListInProgress]) => {
        return {
          blsGmbDifferencesState,
          focusedLocation,
          isUnsavedChange,
          total,
          updatingDiffsInProgress,
          updatingLocationListInProgress,
        };
      }));

  actions = DifferenceActions;
  translatableRows = TRANSLATABLE_ROWS;
  displayedColumns = DISPLAYED_COLUMNS;

  // For rendering the UI columns corresponding to applicable `Actions`
  columnDefinitions = getColumnDefinitions(this.getSelectAllBtnText.bind(this), this.toggleColumn.bind(this));

  //for use in HTML
  SortingEnum = SortingEnum;

  constructor(
      private localStore: BlsGmbDifferencesLocalStore,
      private store: Store,
      private feedbackService: FeedbackService,
      private apiService: ApiService,
      private locationListService: LocationListService,
      private skeletonService: LocationsSkeletonService,
      private blsGmbDifferencesMapService: BlsGmbDifferenceMapService,
      private locationListLocalStore: LocationListLocalStoreService,
      private locationListApi: LocationListApiService) {
    requestAnimationFrame(() => {
      this.tableDataSource = new TableDataSource(this.localStore.state$);
      this.localStore.updateForceAnimation(true);
    });
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  /*************************************************************************************************
   *                                          HANDLERS                                             *
   ************************************************************************************************/

  /**
   * Toggle field name sorting
   * @param sort
   */
  toggleDifferenceSorting(sort: SortingEnum) {
    this.localStore.updateSorting(sort === SortingEnum.ASCENDING ?
        SortingEnum.DESCENDING : sort === SortingEnum.DESCENDING ?
            SortingEnum.NONE : SortingEnum.ASCENDING);
  }

  /**
   * Calls the backend to black-/whitelist a given field, handles the loader,
   * and shows success/error messages accordingly.
   * @param action
   * @param fieldId
   * @param locationId
   */
  performBlacklistOperation(action: 'blacklist' | 'whitelist', fieldId: string, locationId: number) {
    const fieldsToBeBlacklisted = action === 'blacklist' ? [fieldId] : [];
    const fieldsToBeWhitelisted = action === 'whitelist' ? [fieldId] : [];

    this.localStore.updateBlacklistingInProgress(fieldId);

    this.store.select(selectAccountID)
        .pipe(
            take(1),
            switchMap((accountID) => this.apiService
                .blacklistFields(
                    accountID,
                    [locationId],
                    fieldsToBeBlacklisted,
                    fieldsToBeWhitelisted,
                )),
            takeUntil(this.componentDestroyed),
        )
        .subscribe({
          next: (locations) => {
            this.feedbackService.showSuccessSnackbar({
              data: {
                userFriendlyMessage: `${capitalizeFirstLetter(action)}ed "${fieldId}".`,
              }, isAddressListSnackbar: true,
            });

            if (locations?.result?.length) {
              // rehydrate location's state
              // (the entire list might get out of sync though, but refreshing it is too expensive)
              this.store.dispatch(LocationsRefreshActions.patchLocation({ location: locations.result[0] }));
            }

            this.localStore.updateBlacklistingInProgress(null);
          },
          error: ((err: HttpErrorResponse) => {
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: `${capitalizeFirstLetter(action)}ing "${fieldId} failed."`,
                detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
              }, isAddressListSnackbar: true,
            }, err?.status);
            this.localStore.updateBlacklistingInProgress(null);
          }),
        });
  }

  /**
   * Switches between original/translated value
   * If a translated value doesn't exist, will issue a BE request
   * @param diff
   * @param location
   */
  toggleShowTranslation(diff: BlsGmbDifference, location: Location) {
    const diffCopy = cloneDeep(diff);

    diffCopy.showTranslation = !diff.showTranslation;

    // Attempt ot load translations if they're missing
    if (diffCopy.showTranslation && (!diffCopy.blsValueTranslated || !diffCopy.gmbValueTranslated)) {
      // Get location's original diff object - BlsGmbDifference has already mutated data during
      // prepareForDiffChecking(), for instance
      const locationCopy = cloneDeep(location);
      const locationDelta = locationCopy.differences[diff.fieldId];
      const payload: DifferenceTranslationRequestPayload = {
        myBusinessValue: locationDelta.myBusinessValue,
        importedLocationValue: locationDelta.importedLocationValue,
      };

      this.localStore.updateRowsLoadingTranslations(diff.fieldId);
      this.apiService.getDifferenceTranslation(payload)
          .pipe(takeUntil(this.componentDestroyed))
          .subscribe({
            next: ((translatedPayload) => {
              if (translatedPayload.result) {
                this.localStore.updateRowsLoadingTranslations(diff.fieldId);
                diffCopy.blsValueTranslated = prepareForDiffChecking(diff.fieldId,
                    translatedPayload.result.importedLocationValueTranslated);
                diffCopy.gmbValueTranslated = prepareForDiffChecking(diff.fieldId,
                    translatedPayload.result.myBusinessValueTranslated);
                this.localStore.updateDifference(diffCopy);
              }
            }), error: err => {
              this.feedbackService.showAlertSnackbar({
                data: {
                  userFriendlyMessage: `Translating ${this.getFieldNameByFieldID(diff.fieldId)} failed.`,
                  detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                }, isAddressListSnackbar: true,
              }, err?.status);
              this.localStore.updateRowsLoadingTranslations(diff.fieldId);
            },
          });

    } else this.localStore.updateDifference(diffCopy);
  }

  /**
   * Shared wrapper around info menu toggling
   * @param action
   * @param infoMenuVisibility
   */
  toggleInfoMenu(action: DifferenceActions, infoMenuVisibility: InfoMenuVisibilityModel) {
    let infoMenuVisibilityCopy = { ...infoMenuVisibility };
    infoMenuVisibilityCopy[action] = !infoMenuVisibility[action];
    this.localStore.updateInfoMenuVisibility(infoMenuVisibilityCopy);
  }

  /**
   * Toggle table column
   * @param action
   * @param columnVisibility
   */
  toggleColumn(action: DifferenceActions, columnVisibility: ColumnVisibilityModel) {
    let columnVisibilityCopy = { ...columnVisibility };
    columnVisibilityCopy[action] = !columnVisibility[action];
    this.localStore.updateColumnVisibility(columnVisibilityCopy);
  }

  /**
   * Handle both checkbox & display-column cell clicks, accounting for flushing the mutually exclusive
   * column value.
   * Marks the difference as touched - a regular check-circle should be used in the respective row
   * @param column Column definition
   * @param difference Difference between BLS, GMB and Google Update value
   * @param event MouseEvent for checking if its target should trigger a difference-selection or not
   * @param location
   */
  onCell__click(column, difference: BlsGmbDifference, event: MouseEvent, location: Location) {
    // don't select the difference when 'open-in-new-tab'-button was clicked or checkcircle is hidden
    if (wasOpenUrlClicked(event) || column.checkbox.isCheckCircleHidden(difference['fieldId'])) {
      return;
    }

    const action = column.action;
    const differenceCopy = cloneDeep(difference);
    const updateDiff = () => {
      differenceCopy[CorrespondingCheckCircleValue[action]] = !differenceCopy[CorrespondingCheckCircleValue[action]];
      // marking as touched to enable check-circles (if applicable later on)
      differenceCopy.touched = true;

      // unselect other differences selection for a given field even if it's blacklisted
      if (differenceCopy[CorrespondingCheckCircleValue[action]]) {
        flushRemainingDiffs(differenceCopy, action);
      }

      this.localStore.updateDifference(differenceCopy);
    };

    if (this.isFieldPatchableInGmb(difference.fieldId)) {
      if (column.action === DifferenceActions.updateGmb) {
        if (!this.isFieldBlacklisted(difference.fieldId as LSCField, location)) {
          updateDiff();
          return;
        }
      } else {
        updateDiff();
        return;
      }
    }

    // SPECIAL CASE - latLng CAN be selected in this case, however the selection will be
    // ignored in the PATCH/ACCEPT DIFF Call, REVALIDATE coordinates call will be made instead
    // added in BLS-3590
    if (difference.fieldId === COORDINATES_COMPARISON_FIELD_ID && action === DifferenceActions.acceptDifference) {
      updateDiff();
    }

  }

  /**
   * Handle `Select All` clicks, accounting for flushing the mutually exclusive
   * column value.
   * @param action Denoting which column the click originated from
   * @param differences
   * @param location
   */
  onSelectAll__click(action: DifferenceActions, differences: BlsGmbDifference[], location: Location) {
    // determine what triggered the call and thus which action we should take
    const triggerButtonText: 'Select all' | 'Deselect all' = (() => {
      switch (action) {
        case DifferenceActions.updateGmb:
          return this.getUpdateGmb__MULTISELECT_LABEL(differences, location);
        case DifferenceActions.acceptDifference:
          return this.getAcceptDifference__MULTISELECT_LABEL(differences);
        case DifferenceActions.acceptGoogleUpdateValue:
          return this.getAcceptGoogleUpdateValue__MULTISELECT_LABEL(differences);
      }
    })();

    for (const difference of differences) {
      const differenceCopy = cloneDeep(difference);

      differenceCopy.touched = true;

      if (triggerButtonText === 'Select all') {
        const { fieldId } = differenceCopy;

        // Only patchable fields can be ... patched.
        // Other fields aren't patchable just yet
        // -- though the GMB API is prone to changes...
        if (this.isFieldPatchableInGmb(fieldId)) {
          if (action === DifferenceActions.updateGmb) {
            // Only a whitelisted field can be selected for GMB update
            // -- similarly to the comments in `onCell__click`
            if (!this.isFieldBlacklisted(fieldId as LSCField, location)) {
              differenceCopy[CorrespondingCheckCircleValue[action]] = true;
            }
          } else {
            // for all other actions, proceed to set the corresponding diff to `true`
            differenceCopy[CorrespondingCheckCircleValue[action]] = true;
          }
        }

        // SPECIAL CASE - latLng CAN be selected in this case, however the selection will be
        // ignored in the PATCH/ACCEPT DIFF Call, REVALIDATE coordinates call will be made instead
        // added in BLS-3590
        if (fieldId === COORDINATES_COMPARISON_FIELD_ID && action === DifferenceActions.acceptDifference) {
          differenceCopy[CorrespondingCheckCircleValue[action]] = true;
        }

        // unselect other differences selection for a given field even if it is blacklisted
        flushRemainingDiffs(differenceCopy, action);
      } else {
        // we're now in `Deselect all` mode
        // so let's bring the checkbox back to `unselected`
        differenceCopy[CorrespondingCheckCircleValue[action]] = false;
      }

      this.localStore.updateDifference(differenceCopy);
    }
  }

  /**
   * Toggles custom pin size and tracks state
   * @param customPinsMinimized
   */
  togglePinSize(customPinsMinimized: boolean): void {
    this.localStore.updateCustomPinsMinimized(!customPinsMinimized);
    BlsGmbDifferenceMapService.togglePinSize(this.blsGmbDifferencesMapService.getMapArtefacts(), !customPinsMinimized);
  }

  /**
   * Change the data source for this component and scroll data row into view
   * @param rowId
   */
  focusAndScrollRowToView(rowId: string) {
    const nextFocusedRow = this.locationListApi.getRowById(rowId);
    this.store.dispatch(RowActions.updateFocusedRow({ id: Number(nextFocusedRow.id) }));
    this.locationListApi.scrollRowToView(nextFocusedRow);
  }

  /**
   * @param nextRowOneBasedIndex
   * @param focusedLocationId
   * @param isUnsavedChange
   */
  loadNextRow(nextRowOneBasedIndex: number, focusedLocationId: number, isUnsavedChange: boolean) {
    const nextFocusedRow = this.locationListApi
        .getRowByIndex(nextRowOneBasedIndex - 1);

    if (isUnsavedChange) {
      this.skeletonService.showPanelClosingConfirmDialog$('bottom')
          .subscribe((val) => {
            if (val) {
              this.focusAndScrollRowToView(nextFocusedRow.id);
              // reset jumper value
            } else this.jumper.itemIndexInput.nativeElement.value =
                this.getCurrentRowIndex(focusedLocationId,true);
          });
    } else this.focusAndScrollRowToView(nextFocusedRow.id);
  }

  /**
   * HTML wrapper
   */
  toggleContinueToNextLocation() {
    this.localStore.toggleContinueToNextLocation();
  }

  /**
   * @param location (current location)
   */
  undoChanges(location: Location) {
    this.localStore.initDifferenceViewModel(location);
  }

  /**
   * Determines next action - verify coordinates/save diffs/load next location
   */
  onConfirmButtonClick(location: Location, differences: BlsGmbDifference[],
                       isUnsavedChange: boolean,
                       continueToNextLocation: boolean) {
    const payload = this.generatePayload(location, differences);
    const latLngDiff = differences.find(diff => diff.fieldId === COORDINATES_COMPARISON_FIELD_ID);

    // determine nextFocusedLocationId before saving changes, fixes BLS-6374
    const currentRowIndex = this.getCurrentRowIndex(location.id);
    const nextFocusedLocationId =
        this.locationListApi.getRowByIndex(currentRowIndex + 1)?.id;

    if (!nextFocusedLocationId) {
      console.warn('nextFocusedLocationId could not be determined');
    }

    if (latLngDiff && latLngDiff[CorrespondingCheckCircleValue[DifferenceActions.acceptDifference]]) {
      const gmbValue = latLngDiff.gmbValue;
      const coordinates: Coordinate = new Coordinate(gmbValue);

      // don't send this via updateDifferencesAndPatchGMB request,
      // rather trigger a revalidation request instead
      delete payload.differencesToUpdate[COORDINATES_COMPARISON_FIELD_ID];

      this.localStore.updateCoordinateRevalidationInProgress(true);
      // TODO: ASK about this and fix the null param
      this.apiService.verifyCoordinates(location, coordinates, CoordinateSource.GBP)
          .pipe(takeUntil(this.componentDestroyed))
          .subscribe({
            next: (res) => {
              if (res?.result) {
                // rehydrate location's state
                this.store.dispatch(LocationsRefreshActions.patchLocation({ location: res.result }));
              }

              if (isUnsavedChange) {
                this.saveDifferences(location, payload,
                    continueToNextLocation && nextFocusedLocationId ?
                        nextFocusedLocationId :
                        null);
              } else {
                if (continueToNextLocation && nextFocusedLocationId) {
                  this.focusAndScrollRowToView(nextFocusedLocationId);
                }
              }
              this.localStore.updateCoordinateRevalidationInProgress(false);
            }, error: (err) => {
              this.localStore.updateCoordinateRevalidationInProgress(false);
              this.feedbackService.showAlertSnackbar({
                data: {
                  userFriendlyMessage: 'Revalidating coordinates failed',
                  detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                }, isAddressListSnackbar: true,
              }, err?.status);
            },
          });
    } else {
      if (Object.keys(payload.differencesToUpdate).length || payload.fieldMask.length) {
        this.saveDifferences(location, payload,
            continueToNextLocation && nextFocusedLocationId ?
                nextFocusedLocationId :
                null);
      } else if (continueToNextLocation && nextFocusedLocationId) {
        this.focusAndScrollRowToView(nextFocusedLocationId);
      }
    }
  }

  /**
   * When using immutable data src, table will be re-rendered whenever there's a data update,
   * causing rows to be re-rendered - this solves it
   * https://angular.io/api/core/TrackByFunction
   * https://github.com/angular/angular/issues/11727
   * https://github.com/ngrx/store/issues/176
   * @param index
   * @param item
   */
  trackByFn(index: any, item: BlsGmbDifference) {
    return item.fieldId;
  }

  /**
   * Needed for row animation to work correctly
   */
  onAnimationEnd() {
    this.localStore.updateForceAnimation(false);
  }

  /*************************************************************************************************
   *                                          UTILS                                                *
   ************************************************************************************************/


  getCurrentRowIndex(focusedLocationId: number, oneBasedIndex = false): number | undefined {
    if (!focusedLocationId) {
      return undefined;
    }

    const zeroBasedIndex = this.locationListApi.getRowById(focusedLocationId.toString())?.rowIndex;
    return oneBasedIndex ? zeroBasedIndex + 1 : zeroBasedIndex;
  }


  isFieldBlacklisted(fieldId: LSCField, location: Location): boolean {
    return isLocationFieldBlacklisted(fieldId, location);
  }


  getDataSourceIconType$(fieldId: LSCField): Observable<Lowercase<DataSourceStatus>> {
    return this.store.select(locationListSelectors.selectDataSourceIconType(fieldId));
  }

  /**
   * If a difference is untouched && difference.acceptDifferencesSelected = true,
   * an outlined check-circle should be used for the respective gmbValue checkbox
   * @param difference Difference between BLS and GMB
   */
  @memoize()
  isOutlinedCheckCircle(difference: BlsGmbDifference): boolean {
    return !difference.touched && difference.acceptDifferencesSelected;
  }

  /**
   * Returns a "pretty" name for a field ID
   * @param fieldId
   */
  @memoize()
  getFieldNameByFieldID(fieldId: LSCField | string): string {
    return getFullNameForFieldID(fieldId);
  }

  /**
   * Determine if data src bubble should be rendered
   * @param fieldId
   */
  @memoize()
  shouldDisplayDataSourceIcon(fieldId: LSCField | string) {
    return isFieldWithDataSource(fieldId);
  }

  /**
   * Get tooltip key
   * @param dataSourceIconType
   */
  @memoize()
  getDataSourceIconTooltipKey(dataSourceIconType: Lowercase<DataSourceStatus>) {
    return getDataSourceIconTooltipKey(dataSourceIconType);
  }

  /**
   * Get icon name
   * @param dataSourceIconType
   */
  @memoize()
  getDataSourceIconName(dataSourceIconType: Lowercase<DataSourceStatus>) {
    return getDataSourceIconName(dataSourceIconType);
  }

  /**
   * Determining whether a given field can be (un)blacklisted. Blacklist-able is equivalent to
   * patchable but for separation of concerns, this helper makes sense.
   * @param fieldId
   */
  isFieldBlacklistAble(fieldId: string) {
    return this.isFieldPatchableInGmb(fieldId);
  }

  /**
   * Wrapper around `getChangeableGmbFields`.
   * @param fieldId
   */
  @memoize()
  isFieldPatchableInGmb(fieldId: string): boolean {
    return getChangeableGmbFields().includes(fieldId);
  }

  /**
   * Wrapper around the util; accessible in HTML.
   * @param str
   */
  @memoize()
  capitalizeFirstLetter(str: string) {
    return capitalizeFirstLetter(str);
  }

  /**
   * Determines whether a loading spinner should be rendered for the provided fieldId (row)
   * @param fieldId
   * @param rowsLoadingTranslations
   */
  @memoize()
  isTranslationLoading(fieldId: LSCField, rowsLoadingTranslations: string[]) {
    return rowsLoadingTranslations.includes(fieldId);
  }

  /**
   * Determines whether the 'show translation toggle' should be rendered in the for the provided
   * fieldId (row)
   * @param fieldId
   */
  @memoize()
  isTranslatable(fieldId) {
    return this.translatableRows.includes(fieldId);
  }

  /**
   * Right now only the coordinate-related row has extra text. More rows may come.
   * @param fieldId
   */
  @memoize()
  getFieldExplanation(fieldId: string) {
    if (fieldId === COORDINATES_COMPARISON_FIELD_ID) {
      return 'Coordinates cannot yet be updated on GBP from this pop-up. To resolve this difference,' +
          'either revalidate the coordinates in LSC or manually drag the map pin in the GBP UI.';
    }

    return '';
  }

  /**
   * Static wrapper around `getFormattedDifferences` from `diff-utils` accessible from HTML
   * @param difference
   * @private
   */
  @memoize()
  formatDifferencesHandler(difference: BlsGmbDifference): string {
    return getFormattedDifferences({
      gmbValue: difference[CorrespondingBeValue[DifferenceActions.acceptDifference] +
      (difference.showTranslation ? 'Translated' : '')],
      blsValue: difference[CorrespondingBeValue[DifferenceActions.updateGmb] +
      (difference.showTranslation ? 'Translated' : '')],
      fieldId: difference['fieldId'],
    });
  }

  /**
   * Returns an array of lines, where each line contains the HTML diff and an optional URL.
   * @param difference
   * @param action
   */
  @memoize()
  getDifferenceLines(difference: BlsGmbDifference, action: DifferenceActions): {
    html: string,
    url: string | null
  }[] {
    const lines = getStructuredDifferenceLines({
      gmbValue: difference[CorrespondingBeValue[DifferenceActions.acceptDifference] +
      (difference.showTranslation ? 'Translated' : '')],
      blsValue: difference[CorrespondingBeValue[DifferenceActions.updateGmb] +
      (difference.showTranslation ? 'Translated' : '')],
      fieldId: difference['fieldId'],
    });

    return lines.map(line => ({
      html: line.html,
      url: action === DifferenceActions.updateGmb ? line.lscUrl : line.gmbUrl,
    }));
  }

  /**
   * Checks if a string value is an URL or normal text.
   * @param value String to check
   */
  @memoize()
  isValueAnURL(value: string): boolean {
    return isStringAnUrl(value);
  }

  /**
   * Wrapper around the link viability util.
   * @param hrefCandidate
   */
  @memoize()
  prepareUrlHref(hrefCandidate: string): string {
    return prepareUrlHref(hrefCandidate);
  }

  /**
   * Checks is photo preview would be viable.
   * @param srcCandidate
   * @param difference
   */
  @memoize()
  isTextViableImageSrc(srcCandidate: string, difference: BlsGmbDifference): boolean {
    const { fieldId } = difference;
    return this.isValueAnURL(srcCandidate) && ['logoPhoto', 'coverPhoto'].includes(fieldId);
  }

  /**
   * Returns true if all difference fieldIds are blacklisted at which point it is impossible
   * to use the 'Select all' for GMB updates button
   */
  @memoize()
  areAllFieldsBlacklisted(differences: BlsGmbDifference[], location: Location): boolean {
    return differences.map((difference) => difference.fieldId)
        .every((fieldId) => location.blackListedFields
            .find(blacklistedField =>
                // addresses a bug? where BE sometimes returns an alternative fieldID
                blacklistedField === ALTERNATIVE_FIELD_IDS[fieldId] || blacklistedField === fieldId),
        );
  }

  /**
   * Get multi-select button label for the respective column
   * @param action
   * @param differences
   * @param location
   */
  @memoize()
  getSelectAllBtnText(action: DifferenceActions, differences: BlsGmbDifference[], location: Location): string {
    // using anonymous functions as closures to retain the correct
    // `this` context
    switch (action) {
      case DifferenceActions.updateGmb:
        return this.getUpdateGmb__MULTISELECT_LABEL(differences, location);
      case DifferenceActions.acceptDifference:
        return this.getAcceptDifference__MULTISELECT_LABEL(differences);
      case DifferenceActions.acceptGoogleUpdateValue:
        return this.getAcceptGoogleUpdateValue__MULTISELECT_LABEL(differences);
    }
  }

  /**
   * Returns an appropriate update gmb multi-select button label
   */
  @memoize()
  getUpdateGmb__MULTISELECT_LABEL(differences: BlsGmbDifference[], location: Location) {
    if (!differences.length) { // no data
      return 'Select all';
    }

    // EXCLUDE latLng field from the calculation since it cannot be selected
    // (check circle is invisible - added in BLS-3590)
    const whitelistedFields = differences.filter(difference => difference.fieldId !== COORDINATES_COMPARISON_FIELD_ID
        && !this.isFieldBlacklisted(difference.fieldId as LSCField, location));
    if (!whitelistedFields.length) {
      return 'Select all';
    } else {
      return whitelistedFields
          .every(difference => difference.gmbUpdateSelected) ? 'Deselect all' : 'Select all';
    }
  }

  /**
   * Returns an appropriate accept differences multi-select button label
   */
  @memoize()
  getAcceptDifference__MULTISELECT_LABEL(differences: BlsGmbDifference[]) {
    if (!differences.length) { // no data
      return 'Select all';
    }

    return differences
        .every(difference => difference.acceptDifferencesSelected) ? 'Deselect all' : 'Select all';
  }

  /**
   * Returns an appropriate accept google update select button label
   */
  @memoize()
  getAcceptGoogleUpdateValue__MULTISELECT_LABEL(differences: BlsGmbDifference[]) {
    if (!differences.length) { // no data
      return 'Select all';
    }

    return differences
        .every(difference => difference.acceptGoogleValueSelected) ? 'Deselect all' : 'Select all';
  }

  @memoize()
  getConfirmButton__TRANS_KEY(isUnsavedChange: boolean,
                              continueToNextLocation: boolean): string {
    return !isUnsavedChange
    && continueToNextLocation
        ? 'NEXT'
        : 'SAVE';
  }

  @memoize()
  isConfirmButtonDisabled(isUnsavedChange: boolean,
                          continueToNextLocation: boolean,
                          updatingDiffsInProgress: boolean,
                          coordinateRevalidationInProgress: boolean,
                          updatingLocationListInProgress: boolean,
                          total: number) {
    return !isUnsavedChange && !continueToNextLocation ||
        updatingDiffsInProgress ||
        coordinateRevalidationInProgress ||
        updatingLocationListInProgress ||
        !total;
  }

  @memoize()
  isUndoChangesButtonDisabled(isUnsavedChange: boolean,
                              updatingDiffsInProgress: boolean,
                              coordinateRevalidationInProgress: boolean,
                              updatingLocationListInProgress: boolean,
                              total: number) {
    return !isUnsavedChange ||
        updatingDiffsInProgress ||
        coordinateRevalidationInProgress ||
        updatingLocationListInProgress ||
        !total;
  }

  /**
   * Generates the payload for the API call.
   */
  @memoize()
  private generatePayload(location: Location, differences: BlsGmbDifference[]): UpdateDifferencesPayload {
    const payload: UpdateDifferencesPayload = { differencesToUpdate: {}, fieldMask: [] };
    const hasDifferenceBeenAcceptedBefore = (fieldId: string): boolean => {
      let oldDifference = location.differences[fieldId];
      return oldDifference.ignored;
    };

    let fieldId = '';
    for (const difference of differences) {
      fieldId = difference.fieldId;
      if (difference.gmbUpdateSelected) {
        // For all intents and purposes, we're limiting the values that can be passed onto GMB
        // coming from LSC only to those that are white-listed (i.e. are NOT blacklisted).
        // This edge case of course only applies to the first, `updateGmb` column.
        if (!this.isFieldBlacklisted(difference.fieldId as LSCField, location)) {
          payload.fieldMask.push(fieldId);
        }
      } else if (
          hasDifferenceBeenAcceptedBefore(fieldId) !==
          difference.acceptDifferencesSelected
      ) {
        // accept differences choice has changed
        payload.differencesToUpdate[fieldId] = difference.acceptDifferencesSelected;
      }
    }
    return payload;
  }

  private saveDifferences(location: Location,
                          payload: UpdateDifferencesPayload,
                          nextFocusedLocationId?: string): void {
    this.store.dispatch(OngoingActions.addOngoingAction({
      ongoingAction:
      OngoingActionsEnum.UPDATE_DIFFERENCES_AND_PATCH_GBP,
    }));

    this.store.select(selectAccountID)
        .pipe(
            take(1),
            switchMap((accountID) => this.apiService.updateDifferencesAndPatchGMB(
                accountID,
                location.id,
                payload,
            )),
            takeUntil(this.componentDestroyed),
        )
        .subscribe({
          next: (res) => {
            this.store.dispatch(OngoingActions.removeOngoingAction({
              ongoingAction: OngoingActionsEnum.UPDATE_DIFFERENCES_AND_PATCH_GBP,
            }));

            if (res?.result) {
              // rehydrate location's state
              this.store.dispatch(LocationsRefreshActions.patchLocation({ location: res.result }));
            }

            if (nextFocusedLocationId) {
              this.focusAndScrollRowToView(nextFocusedLocationId);
            }
            this.feedbackService.showSuccessSnackbar({
              data: { userFriendlyMessage: 'Editing location difference data has been successful!' },
              isAddressListSnackbar: true,
            });
          },
          error: (error: HttpErrorResponse) => {
            // we're dealing w/ a HTTP error wrapped in `HttpErrorResponse` so let's make
            // sure we're consolidating the "right" response
            const typedError = error.error as ApiResponseWithPotentialErrors;
            const errorMessages = this.locationListService.consolidateApiResponseErrors(typedError);

            this.store.dispatch(OngoingActions.removeOngoingAction({
              ongoingAction: OngoingActionsEnum.UPDATE_DIFFERENCES_AND_PATCH_GBP,
            }));

            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: 'Editing location differences failed.',
                detailedInfoFromBE: errorMessages,
              },
              isAddressListSnackbar: true,
            }, error.status);
          },
        });
  }


}
